/**
 * fuel-load.ts
 * add-locx-trip-fuel-tracking PR1 — contracts for servicios.fuel_load
 * ("carga de combustible" in the legacy BRAVA system).
 *
 * A dispatcher records a fuel load from a tanker (cisterna) asset to a
 * receiver asset (vehicle), against a cost center, with liters + optional
 * hours/km, and a receiver driver that is EITHER an internal employee XOR an
 * external free-text name. The receiver signs (canvas) to confirm — the
 * signature is OPTIONAL in v1 and attached in a second step via
 * AttachFuelLoadSignatureUseCase (mirrors "fotos por falla" in
 * add-locx-fleet-failure-reports).
 *
 * fuel_load has NO FSM — it is a flat operational record (design.md §2). The
 * only state-like concept is `signatureFileId IS NULL` (pending) vs. not-null
 * (signed), exposed as a plain nullable field, never as identity.state.
 *
 * Spec: openspec/changes/add-locx-trip-fuel-tracking/design.md §3/§4
 */
import { z } from 'zod';
import { CapturedAtSchema, ClientRequestIdSchema } from '@suite/contracts-kernel';

// ---------------------------------------------------------------------------
// Create — receiver is EITHER an internal employee XOR an external name.
// Zod superRefine enforces EXACTLY ONE (not zero, not both) — defense-in-depth
// #1. CreateFuelLoadUseCase re-checks the same rule before any write (#2). The
// DB CHECK constraint (migration 0103) only guards against BOTH being set —
// it allows neither, since "exactly one" is an application-layer rule, not a
// DB-layer invariant (design.md §3/§4 — same 3-layer posture as fleet_failure's
// assignedEmploymentId/externalProviderId, but with a stricter application rule).
// ---------------------------------------------------------------------------

export const FuelLoadCreateSchema = z
  .object({
    tankerAssetId: z.string().uuid(),
    receiverAssetId: z.string().uuid(),
    receiverEmploymentId: z.string().uuid().optional(),
    receiverExternalName: z.string().min(1).optional(),
    costCenterId: z.string().uuid(),
    liters: z.coerce.number().positive('liters must be > 0'),
    hours: z.coerce.number().nonnegative('hours must be ≥ 0').optional(),
    kilometers: z.coerce.number().nonnegative('kilometers must be ≥ 0').optional(),
    comment: z.string().optional(),
    /**
     * locx-paridad-operativa-bramix Slice B (MR-3.2/D3) — non-empty
     * justification for a missing REQUIRED reading (per the receiver asset
     * type's tracksHours/tracksKm). Whitespace-only is rejected by
     * `.trim().min(1)`, mirroring `assertMeterReadings`'s `isBlank` check —
     * capture-at-create ONLY, never accepted on any update schema (D3, "el
     * motivo nunca queda stale"). The cross-field rule itself (is a reading
     * actually required?) cannot live in Zod — enforced by
     * CreateFuelLoadUseCase's guard, not here.
     */
    meterReadingMissingReason: z.string().trim().min(1).max(500).optional(),
    // operator-offline-forms — idempotency key and phone capture time
    // (becomes loaded_at inside a 72 h window). Both optional (S4).
    clientRequestId: ClientRequestIdSchema.optional(),
    capturedAt: CapturedAtSchema.optional(),
    // dispatchedByUserId NEVER here — from TenantContext.userId (KC-3.5).
    // signatureFileId NEVER here — attached in a separate step (AttachFuelLoadSignatureUseCase).
  })
  .superRefine((data, ctx) => {
    const hasEmployment = data.receiverEmploymentId !== undefined;
    const hasExternal = data.receiverExternalName !== undefined;
    if (hasEmployment && hasExternal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['receiverExternalName'],
        message: 'receiverEmploymentId and receiverExternalName are mutually exclusive — set exactly one.',
      });
    }
    if (!hasEmployment && !hasExternal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['receiverEmploymentId'],
        message: 'Exactly one of receiverEmploymentId/receiverExternalName is required.',
      });
    }
  });
export type FuelLoadCreate = z.infer<typeof FuelLoadCreateSchema>;

// ---------------------------------------------------------------------------
// Attach signature — a fileId already uploaded via the generic kernel
// POST /files endpoint (mirrors AttachFleetFailurePhotosSchema's fileIds — this
// is the singular-signature counterpart, one file per fuel_load, not N).
// ---------------------------------------------------------------------------

export const AttachFuelLoadSignatureSchema = z
  .object({
    fileId: z.string().uuid(),
  })
  .strict();
export type AttachFuelLoadSignatureInput = z.infer<typeof AttachFuelLoadSignatureSchema>;

// ---------------------------------------------------------------------------
// Response
// ---------------------------------------------------------------------------

export const FuelLoadResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  tankerAssetId: z.string().uuid(),
  receiverAssetId: z.string().uuid(),
  receiverEmploymentId: z.string().uuid().nullable(),
  receiverExternalName: z.string().nullable(),
  // dispatchedByUserId: nullable + source/dispatchedByName since migration 0113 —
  // null ONLY for source='legacy_import' rows (the historical carga_combustible
  // import), same posture as fleet-failure.ts's reportedByUserId/reportedByName.
  dispatchedByUserId: z.string().uuid().nullable(),
  source: z.enum(['app', 'legacy_import']),
  dispatchedByName: z.string().nullable(),
  costCenterId: z.string().uuid(),
  liters: z.string(),
  hours: z.string().nullable(),
  kilometers: z.string().nullable(),
  loadedAt: z.string().datetime().or(z.date()),
  signatureFileId: z.string().uuid().nullable(),
  comment: z.string().nullable(),
  /**
   * locx-paridad-operativa-bramix Slice B (MR-3.2/D3) — non-null means this
   * row is pending workshop review (a required reading was missing at create
   * time and the operator justified it). `null` = every required reading was
   * present, or none was required.
   */
  meterReadingMissingReason: z.string().nullable(),
  createdAt: z.string().datetime().or(z.date()),
});
export type FuelLoadResponse = z.infer<typeof FuelLoadResponseSchema>;

// ---------------------------------------------------------------------------
// FuelLoadListItemResponse — GET /servicios/fuel-loads (admin report, 23 jul
// 2026). Additive over FuelLoadResponse — denormalized human labels so the
// grid never renders a raw uuid (apps/frontend/CLAUDE.md), same posture as
// FleetFailureReportResponse's assetSerialNumber/costCenterName/
// reportedByUserName. receiverName is ALWAYS a resolved string (never null):
// FuelLoadCreateSchema's superRefine guarantees exactly one of
// receiverEmploymentId/receiverExternalName is set, so one branch or the
// other always resolves.
// ---------------------------------------------------------------------------

export const FuelLoadListItemResponseSchema = FuelLoadResponseSchema.extend({
  tankerAssetSerialNumber: z.string(),
  receiverAssetSerialNumber: z.string(),
  costCenterName: z.string().nullable(),
  dispatchedByUserName: z.string().nullable(),
  receiverName: z.string(),
});
export type FuelLoadListItemResponse = z.infer<typeof FuelLoadListItemResponseSchema>;
